import { useAppStore, selectActiveProfile } from '@/store';
import allergensData from '@/data/allergens.json';
import type { AllergenEntry } from '@allergen-lens/shared-logic';
import { RegulatoryMode } from '@allergen-lens/shared-logic';
import { Tooltip } from './Tooltip';
import styles from './AllergenGrid.module.css';

export function AllergenGrid() {
  const activeProfile = useAppStore(selectActiveProfile);
  const toggleProfileAllergen = useAppStore((s) => s.toggleProfileAllergen);
  const mode = useAppStore((s) => s.mode);

  const entries = allergensData as AllergenEntry[];

  // Filter by regulatory mode using isEU/isUS fields
  const filtered = entries.filter((e) => {
    if (mode === RegulatoryMode.EU) return e.isEU;
    return e.isUS;
  });

  // Group by regulatory region
  const groups = new Map<string, AllergenEntry[]>();
  for (const entry of filtered) {
    const group = entry.isEU ? 'eu' : 'us';
    const list = groups.get(group) ?? [];
    list.push(entry);
    groups.set(group, list);
  }

  if (!activeProfile) return null;

  const selected = activeProfile.selectedAllergens;

  return (
    <div>
      {[...groups.entries()].map(([groupKey, groupEntries]) => (
        <div key={groupKey}>
          <div className={styles.groupLabel}>
            {groupKey === 'eu' ? (
              <>EU — 14 Allergens <Tooltip content="Mandatory declarable allergens per EU Regulation 1169/2011 Annex II. Cereals containing gluten, crustaceans, eggs, fish, peanuts, soybeans, milk, tree nuts, celery, mustard, sesame, sulphites, lupin, molluscs." /></>
            ) : (
              <>US — 9 Allergens <Tooltip content="Major food allergens per US FALCPA + FASTER Act (2023). Milk, eggs, fish, crustacean shellfish, tree nuts, peanuts, wheat, soybeans, sesame." /></>
            )}
          </div>
          <div className={styles.grid}>
            {groupEntries.map((entry) => {
              const isSelected = selected.includes(entry.id);
              return (
                <div
                  key={entry.id}
                  className={`${styles.gridItem} ${isSelected ? styles.gridItemSelected : ''}`}
                  onClick={() => toggleProfileAllergen(entry.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleProfileAllergen(entry.id); } }}
                  role="checkbox"
                  aria-checked={isSelected}
                  aria-label={entry.canonicalName}
                  tabIndex={0}
                >
                  <input
                    type="checkbox"
                    className={styles.gridCheckbox}
                    checked={isSelected}
                    onChange={() => toggleProfileAllergen(entry.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {entry.canonicalName}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
